import {
  Stack,
  Text,
  Box,
  useTheme,
  IconButton,
} from '@ltvco/refresh-lib/theme';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Balance,
  AccessTime,
} from '@mui/icons-material';
import { useContext, useEffect, useRef, useState } from 'react';

import { ChatContext } from 'components/ChatBV/context';
import { formatCourtDateWithTime } from 'routes/Dashboard/CourtCases/v2/utils';

import { BoldSpan } from '../SummaryComponent/ReportRenderers/components';

import {
  FadedText,
  GrayBorderStack,
  ToggleButton,
  ToggleButtonBox,
} from './components';
import { MessageDocketEntries } from './MessageDocketEntries';
import { MessagePartiesAttorneys } from './MessagePartiesAttorneys';

export const CourtCaseMessageContent = ({
  courtCaseData,
}: {
  courtCaseData: any;
}) => {
  const theme = useTheme();
  const { collapseAllSectionsTrigger } = useContext(ChatContext);
  const [collapse, setCollapse] = useState<boolean>(false);
  const [partiesAttorneys, setPartiesAttorneys] = useState<boolean>(false);
  const prevTriggerRef = useRef(collapseAllSectionsTrigger);

  useEffect(() => {
    if (prevTriggerRef.current !== collapseAllSectionsTrigger) {
      setCollapse(true);
      prevTriggerRef.current = collapseAllSectionsTrigger;
    }
  }, [collapseAllSectionsTrigger]);

  return (
    <Stack spacing={2} margin={'24px 0 !important'}>
      <Text>
        We found details for the court case{' '}
        <BoldSpan>{courtCaseData?.name}</BoldSpan>. This case has{' '}
        <BoldSpan>{courtCaseData?.docketEntries?.length}</BoldSpan> docket
        entries.
      </Text>
      <Stack spacing={1}>
        <BoldSpan>Docket Summary</BoldSpan>
        <Text>
          <BoldSpan>Docket Entries</BoldSpan>:{' '}
          {courtCaseData?.docketEntries?.length}
        </Text>
      </Stack>

      <GrayBorderStack
        spacing={2}
        sx={
          collapse
            ? {
                borderColor: `${theme.palette.primary.main}1F`,
                background: `linear-gradient(90deg, ${theme.palette.primary.contrastText} 0%, ${theme.palette.primary.light} 100%)`,
              }
            : {}
        }
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Balance />
            <Text fontWeight="bold" fontSize={20}>
              Case
            </Text>
          </Box>
          {collapse ? (
            <IconButton
              onClick={() => setCollapse(false)}
              aria-label="Expand case details"
              size="small"
            >
              <KeyboardArrowDown />
            </IconButton>
          ) : (
            <IconButton
              onClick={() => setCollapse(true)}
              aria-label="Collapse case details"
              size="small"
            >
              <KeyboardArrowUp />
            </IconButton>
          )}
        </Box>
        <Stack spacing={0.5} px={1}>
          <Text variant="h4" sx={{ mb: 0 }}>
            {courtCaseData?.name || ''}
          </Text>
          <FadedText variant="body1">
            {courtCaseData?.courtName || 'Not Available'}
          </FadedText>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTime sx={{ fontSize: 16 }} />
            <FadedText variant="body1">
              Last Updated:{' '}
              {formatCourtDateWithTime(courtCaseData?.dateModified)}
            </FadedText>
          </Box>
        </Stack>
        {!collapse ? (
          <>
            <Stack spacing={0.5} px={1}>
              {courtCaseData?.assignedTo && (
                <Text>
                  <BoldSpan>Assigned To:</BoldSpan> {courtCaseData?.assignedTo}
                </Text>
              )}
              {courtCaseData?.referredTo && (
                <Text>
                  <BoldSpan>Referred To:</BoldSpan> {courtCaseData?.referredTo}
                </Text>
              )}
              {courtCaseData?.docketNumber &&
              courtCaseData?.courtId &&
              courtCaseData.name ? (
                <Text>
                  <BoldSpan>Citation:</BoldSpan> {courtCaseData?.name},{' '}
                  {courtCaseData?.docketNumber}, (
                  {courtCaseData?.courtId?.toUpperCase()})
                </Text>
              ) : null}
              {courtCaseData?.dateFiled && (
                <Text>
                  <BoldSpan>Date Filed:</BoldSpan>{' '}
                  {formatCourtDateWithTime(courtCaseData?.dateFiled)}
                </Text>
              )}
              {courtCaseData?.dateTerminated && (
                <Text>
                  <BoldSpan>Date Terminated:</BoldSpan>{' '}
                  {formatCourtDateWithTime(courtCaseData?.dateTerminated)}
                </Text>
              )}
              {courtCaseData?.dateLastFiling && (
                <Text>
                  <BoldSpan>Date of Last Known Filing:</BoldSpan>{' '}
                  {formatCourtDateWithTime(courtCaseData?.dateLastFiling)}
                </Text>
              )}
              {courtCaseData?.cause && (
                <Text>
                  <BoldSpan>Cause:</BoldSpan> {courtCaseData?.cause}
                </Text>
              )}
              {courtCaseData?.natureOfSuit && (
                <Text>
                  <BoldSpan>Nature of Suit:</BoldSpan>{' '}
                  {courtCaseData?.natureOfSuit}
                </Text>
              )}
              {courtCaseData?.juryDemand && (
                <Text>
                  <BoldSpan>Jury Demand:</BoldSpan> {courtCaseData?.juryDemand}
                </Text>
              )}
              {courtCaseData?.jurisdictionType && (
                <Text>
                  <BoldSpan>Jurisdiction Type:</BoldSpan>{' '}
                  {courtCaseData?.jurisdictionType}
                </Text>
              )}
            </Stack>
            <ToggleButtonBox>
              <ToggleButton
                variant={!partiesAttorneys ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setPartiesAttorneys(false)}
                aria-pressed={!partiesAttorneys}
                sx={
                  !partiesAttorneys
                    ? {
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        boxShadow: '0px 2px 1px -1px #00000033',
                        '&:hover': {
                          backgroundColor: theme.palette.background.paper,
                        },
                        '&:focus-visible': {
                          outline: `2px solid ${theme.palette.primary.main}`,
                          outlineOffset: '2px',
                        },
                      }
                    : {}
                }
              >
                Docket Entries
              </ToggleButton>
              <ToggleButton
                variant={partiesAttorneys ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setPartiesAttorneys(true)}
                aria-pressed={partiesAttorneys}
                sx={
                  partiesAttorneys
                    ? {
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        boxShadow: '0px 2px 1px -1px #00000033',
                        '&:hover': {
                          backgroundColor: theme.palette.background.paper,
                        },
                        '&:focus-visible': {
                          outline: `2px solid ${theme.palette.primary.main}`,
                          outlineOffset: '2px',
                        },
                      }
                    : {}
                }
              >
                Parties & Attorneys
              </ToggleButton>
            </ToggleButtonBox>
            {partiesAttorneys ? (
              <MessagePartiesAttorneys
                courtCaseId={courtCaseData?.courtCaseId || ''}
              />
            ) : (
              <MessageDocketEntries
                docketEntries={courtCaseData?.docketEntries}
                courtCaseId={courtCaseData?.courtCaseId || ''}
              />
            )}
            <FadedText fontSize={12}>
              <BoldSpan>PLEASE BE ADVISED:</BoldSpan> Court case information
              contained in our reports may not always be 100% up-to-date,
              accurate or complete, since data is pulled from records maintained
              by various state, county, municipal and other government agencies
              and their contents may not be fully digitized, updated or
              comprehensive. Please use our results as no more than a point of
              departure and certainly no substitute for your own due diligence.
            </FadedText>
          </>
        ) : null}
      </GrayBorderStack>
    </Stack>
  );
};
